
import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { 
  Search, Send, Paperclip, FileText, ArrowLeft,
  Sun, Moon, Users, ShieldCheck, 
  Clock, Crown, Plus, Settings, Bell, BellOff,
  Image as ImageIcon,
  RefreshCw, MessageSquare, Check, ShieldAlert, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { User as FirebaseUser } from 'firebase/auth';
import { ReportTemplate, UserProfile, PeerMessage, PeerChat, WorkGroup, WorkGroupMessage, NotificationSettings } from '../types';
import { 
  subscribeToDirectory, subscribeToPeerChat, sendPeerMessage, 
  subscribeToActiveChats,
  saveReportToCloud,
  createWorkGroup, subscribeToWorkGroups, sendWorkGroupMessage, subscribeToWorkGroupMessages,
  saveUserProfile, subscribeToUserProfile,
  subscribeToPublicWorkGroups, sendGroupJoinRequest, subscribeToGroupRequests, handleGroupJoinRequest,
  proposeAdminTransfer, acceptAdminRole, leaveWorkGroup, testFirestoreConnection
} from '../services/db';
import { GroupJoinRequest } from '../types';
import { sanitizeHtml } from '../utils/sanitize';
import Modal from './Modal';

// Utilidad para obtener el rango profesional del usuario
const getUserRank = (u: UserProfile) => {
  const isMasterUser = (u.email || "").toLowerCase().trim() === 'dr.francisco.reyes@gmail.com' || u.uid === 'guest_dev_offline';
  if (isMasterUser) return 'RADIOLOGY MASTER';
  if (u.role === 'RESIDENT') return `Residente R${u.residentYear || 1}`;
  const years = u.experienceYears;
  const hasOtherSpec = (u.otherMedicalSpecialty || "").trim() !== "";
  if (years === 'más de 30 años') return 'Maestro de la Radiología';
  if (years === '20 a 30') return 'Consultor en Jefe';
  if (years === '10 a 20') return 'Radiólogo Experto';
  if (years === '5 a 10' || (years === 'menos de 5 años' && hasOtherSpec)) return 'Radiólogo Senior';
  return 'Médico Radiólogo';
};

const isHighRank = (u: UserProfile) => {
  const rank = getUserRank(u);
  return rank === 'RADIOLOGY MASTER' || rank === 'Maestro de la Radiología' || rank === 'Consultor en Jefe';
};

const compressImage = (base64: string, maxWidth = 1200): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = base64;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      if (width > maxWidth) {
        height = (maxWidth / width) * height;
        width = maxWidth;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.6));
    };
  });
};

// Componente de entrada de chat memoizado para evitar parpadeos al escribir
const ChatInput = memo(({ 
  onSendMessage, 
  isDarkMode, 
  isSending, 
  onOpenAttachment,
  disabled
}: { 
  onSendMessage: (text: string) => void; 
  isDarkMode: boolean; 
  isSending: boolean; 
  onOpenAttachment: () => void;
  disabled?: boolean;
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleSend = () => {
    if (disabled || (!inputValue.trim() && !isSending)) return;
    onSendMessage(inputValue);
    setInputValue('');
  };

  return (
    <div className={`flex items-end gap-2 max-w-full rounded-[20px] p-1.5 border shadow-sm transition-opacity ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-gray-50 border-gray-100'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
      <button 
        onClick={onOpenAttachment} 
        disabled={disabled}
        className={`p-2.5 transition-all ${disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-indigo-500'}`}
      >
        <Paperclip size={18}/>
      </button>
      <textarea 
        value={inputValue} 
        onChange={e => setInputValue(e.target.value)} 
        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())} 
        placeholder={disabled ? "Disponible tras validación del Master" : "Mensaje clínico..."} 
        disabled={disabled}
        className={`flex-1 bg-transparent border-none outline-none text-sm p-2 max-h-24 min-h-[40px] resize-none font-medium ${disabled ? 'cursor-not-allowed' : ''}`} 
        rows={1} 
      />
      <button 
        onClick={handleSend} 
        disabled={disabled || (!inputValue.trim() && !isSending) || isSending} 
        className={`p-2.5 rounded-xl transition-all ${!disabled && (inputValue.trim() || isSending) ? 'bg-indigo-600 text-white shadow-lg active:scale-95' : 'text-gray-300'}`}
      >
        {isSending ? <RefreshCw size={18} className="animate-spin" /> : <Send size={18}/>}
      </button>
    </div>
  );
});

const MessagingScreen: React.FC<{ user: FirebaseUser | null; reports: ReportTemplate[]; isVerified: boolean }> = ({ user, reports, isVerified }) => {
  const getInitialTheme = () => {
    const saved = localStorage.getItem('module_theme_community');
    if (saved) return saved === 'dark';
    return localStorage.getItem('app_theme') === 'dark';
  };

  const [isDarkMode, setIsDarkMode] = useState(getInitialTheme());

  useEffect(() => {
    const handleThemeChange = () => {
       const saved = localStorage.getItem('module_theme_community');
       if (!saved) {
         setIsDarkMode(localStorage.getItem('app_theme') === 'dark');
       }
    };
    window.addEventListener('app_theme_changed', handleThemeChange);
    return () => window.removeEventListener('app_theme_changed', handleThemeChange);
  }, []);

  const toggleDarkMode = () => {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem('module_theme_community', next ? 'dark' : 'day');
    window.dispatchEvent(new Event('module_theme_changed'));
  };

  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [directoryError, setDirectoryError] = useState<string | null>(null);
  const [activeChats, setActiveChats] = useState<PeerChat[]>([]);
  const [workGroups, setWorkGroups] = useState<WorkGroup[]>([]);
  const [publicGroups, setPublicGroups] = useState<WorkGroup[]>([]);
  const [pendingRequests, setPendingRequests] = useState<Record<string, GroupJoinRequest[]>>({});
  const [mySentRequests, setMySentRequests] = useState<GroupJoinRequest[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<{ success: boolean; message: string } | null>(null);
  
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<WorkGroup | null>(null);

  const [isGroupManagementOpen, setIsGroupManagementOpen] = useState(false);
  const [selectedGroupToManage, setSelectedGroupToManage] = useState<WorkGroup | null>(null);
  const [newGroupIsPublic, setNewGroupIsPublic] = useState(true);
  const [messages, setMessages] = useState<PeerMessage[]>([]);
  const [groupMessages, setGroupMessages] = useState<WorkGroupMessage[]>([]);
  const [searchText, setSearchText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isDirectoryLoading, setIsDirectoryLoading] = useState(true);
  
  const [activeTab, setActiveTab] = useState<'CHATS' | 'GROUPS'>('CHATS');
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [attachmentTab, setAttachmentTab] = useState<'REPORTS' | 'IMAGES'>('REPORTS');
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const PRESENCE_THRESHOLD_MS = 5 * 60 * 1000;

  const [retryCount, setRetryCount] = useState(0);

  const groupRequestsUnsubsRef = useRef<Record<string, () => void>>({});

  useEffect(() => {
    if (!user) return;
    setIsDirectoryLoading(true);
    const unsubChats = subscribeToActiveChats(user.uid, setActiveChats);
    const unsubGroups = subscribeToWorkGroups(user.uid, (groups) => {
      setWorkGroups(groups);
      
      // Manejar suscripciones a solicitudes de unión de forma eficiente
      const currentAdminGroups = groups.filter(g => g.admins.includes(user.uid));
      const currentAdminGroupIds = new Set(currentAdminGroups.map(g => g.id));

      // Limpiar suscripciones de grupos que ya no administro
      Object.keys(groupRequestsUnsubsRef.current).forEach(groupId => {
        if (!currentAdminGroupIds.has(groupId)) {
          groupRequestsUnsubsRef.current[groupId]();
          delete groupRequestsUnsubsRef.current[groupId];
          setPendingRequests(prev => {
            const next = { ...prev };
            delete next[groupId];
            return next;
          });
        }
      });

      // Añadir suscripciones para nuevos grupos administrados
      currentAdminGroups.forEach(g => {
        if (!groupRequestsUnsubsRef.current[g.id]) {
          groupRequestsUnsubsRef.current[g.id] = subscribeToGroupRequests(g.id, (reqs) => {
            setPendingRequests(prev => ({ ...prev, [g.id]: reqs }));
          });
        }
      });
    });
    const unsubPublic = subscribeToPublicWorkGroups(setPublicGroups);
    const unsubProfile = subscribeToUserProfile(user.uid, setUserProfile);
    
    // Test connection
    testFirestoreConnection(user.uid).then(setConnectionStatus);

    const unsubDirectory = subscribeToDirectory((users, error) => {
        if (error) {
          setDirectoryError(error);
        } else {
          setDirectoryError(null);
        }
        setAllUsers(users.filter(u => u.uid));
        setIsDirectoryLoading(false);
    });
    return () => { 
      unsubChats(); 
      unsubGroups(); 
      unsubPublic(); // Añadido cleanup faltante
      unsubDirectory(); 
      unsubProfile(); 
      // Limpiar todas las suscripciones a solicitudes
      Object.values(groupRequestsUnsubsRef.current).forEach(unsub => unsub());
      groupRequestsUnsubsRef.current = {};
    };
  }, [user, retryCount]);

  // Manejar apertura de chat desde otros módulos (Dashboard/Perfil)
  useEffect(() => {
    if (allUsers.length > 0) {
      const pendingUserId = localStorage.getItem('modular_open_chat_user_id');
      if (pendingUserId) {
        const targetUser = allUsers.find(u => u.uid === pendingUserId);
        if (targetUser) {
          setSelectedUser(targetUser);
          setSelectedGroup(null);
          setActiveTab('CHATS');
          localStorage.removeItem('modular_open_chat_user_id');
        }
      }
    }
  }, [allUsers]);

  // SUSCRIPCIÓN INDEPENDIENTE POR CHAT P2P
  useEffect(() => {
    if (!user || !selectedUser) { 
      setMessages([]); 
      return; 
    }
    
    setMessages([]);
    const chatId = [user.uid, selectedUser.uid].sort().join('_');
    const unsubscribe = subscribeToPeerChat(chatId, (newMsgs) => {
      const now = Date.now();
      setMessages(newMsgs.filter(m => !m.expiresAt || m.expiresAt > now));
    });
    
    return () => unsubscribe();
  }, [selectedUser, user]);

  // SUSCRIPCIÓN INDEPENDIENTE POR GRUPO
  useEffect(() => {
    if (!user || !selectedGroup) {
      setGroupMessages([]);
      return;
    }
    setGroupMessages([]);
    const unsubscribe = subscribeToWorkGroupMessages(selectedGroup.id, setGroupMessages);
    return () => unsubscribe();
  }, [selectedGroup, user]);

  useEffect(() => {
    if (messages.length > 0 || groupMessages.length > 0) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, groupMessages]);

  const handleSendMessage = useCallback(async (textOverride?: string, sharedContent?: any) => {
    if (!user || (!selectedUser && !selectedGroup) || isSending) return;
    if (!isVerified) {
      alert("Tu cuenta aún no ha sido validada por el equipo Master. No puedes enviar mensajes hasta completar la validación.");
      return;
    }
    const text = (textOverride !== undefined ? textOverride : '').trim();
    if (!text && !sharedContent) return;

    setIsSending(true);
    
    try {
      let finalContent = sharedContent;
      let expiresAt = null;

      if (sharedContent?.type === 'IMAGE') {
        const compressed = await compressImage(sharedContent.data);
        finalContent = { ...sharedContent, data: compressed };
        expiresAt = Date.now() + (24 * 60 * 60 * 1000);
      }
      
      if (selectedUser) {
        const chatId = [user.uid, selectedUser.uid].sort().join('_');
        const newMsg: PeerMessage = { 
          id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, 
          senderId: user.uid, 
          text: text, 
          timestamp: Date.now(),
          ...(expiresAt && { expiresAt }),
          ...(finalContent && { sharedContent: finalContent })
        };
        await sendPeerMessage(chatId, newMsg);
      } else if (selectedGroup) {
        const newMsg: Omit<WorkGroupMessage, 'id' | 'timestamp'> = {
          groupId: selectedGroup.id,
          senderId: user.uid,
          senderName: userProfile?.displayName || user.displayName || 'Radiólogo',
          text: text,
          ...(finalContent && { sharedContent: finalContent })
        };
        await sendWorkGroupMessage(selectedGroup.id, newMsg);
      }
      
      setIsAttachmentModalOpen(false);
    } catch (e: any) {
      console.error(e);
      alert("El envío del mensaje ha fallado. Por favor, verifique su conexión a la red hospitalaria.");
    } finally {
      setIsSending(false);
    }
  }, [user, selectedUser, selectedGroup, isSending, userProfile, isVerified]);

  const handleCreateGroup = async () => {
    if (!user || !newGroupName.trim()) return;
    try {
      await createWorkGroup({
        name: newGroupName,
        description: newGroupDesc,
        members: [user.uid],
        admins: [user.uid],
        createdBy: user.uid,
        isPublic: newGroupIsPublic
      });
      setIsCreateGroupModalOpen(false);
      setNewGroupName('');
      setNewGroupDesc('');
      setSelectedMembers([]);
    } catch (e) {
      console.error("Error creating group:", e);
    }
  };

  const handleJoinPublicGroup = async (group: WorkGroup) => {
    if (!user || !userProfile) return;
    if (!isVerified) {
      alert("Tu cuenta aún no ha sido validada por el equipo Master. No puedes solicitar unirte a grupos hasta completar la validación.");
      return;
    }
    try {
      await sendGroupJoinRequest({
        groupId: group.id,
        groupName: group.name,
        userId: user.uid,
        userName: userProfile.displayName || user.displayName || 'Radiólogo',
        userEmail: userProfile.email || user.email || '',
        userPhoto: userProfile.photoURL || undefined,
        status: 'PENDING',
        type: 'JOIN_REQUEST'
      });
      alert(`Solicitud enviada a ${group.name}`);
    } catch (e) {
      console.error("Error joining group:", e);
    }
  };

  const updateNotificationSettings = async (settings: Partial<NotificationSettings>) => {
    if (!user || !userProfile) return;
    const current = userProfile.notificationSettings || { communityToasts: true, groupToasts: true, mutedGroups: [] };
    const updated: NotificationSettings = { ...current, ...settings };
    await saveUserProfile({ ...userProfile, notificationSettings: updated, updatedAt: Date.now() });
  };

  const toggleGroupMute = async (groupId: string) => {
    if (!user || !userProfile) return;
    const currentMuted = userProfile.notificationSettings?.mutedGroups || [];
    const isMuted = currentMuted.includes(groupId);
    const nextMuted = isMuted ? currentMuted.filter(id => id !== groupId) : [...currentMuted, groupId];
    await updateNotificationSettings({ mutedGroups: nextMuted });
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      handleSendMessage('', { type: 'IMAGE', data: reader.result, title: 'Imagen Diagnóstica' });
    };
    reader.readAsDataURL(file);
  };

  const contactSections = useMemo(() => {
    if (!user) return { recent: [], directory: [] };

    const isMaster = userProfile?.role === 'RADIOLOGY MASTER' || 
                    userProfile?.displayName?.toLowerCase().includes('master') ||
                    (user?.email || "").toLowerCase().trim() === 'dr.francisco.reyes@gmail.com';

    // RESTRICCIÓN DE SEGURIDAD: Si el usuario no está verificado, solo se ve a sí mismo (excepto si es Master)
    if (!isVerified && !isMaster) {
       const me = allUsers.find(u => u.uid === user.uid);
       return { recent: [], directory: me ? [me] : [] };
    }

    const term = searchText.toLowerCase();
    
    const directory = allUsers.filter(u => u.uid !== user.uid && (
      (u.displayName || "").toLowerCase().includes(term) || 
      (u.mainSpecialty || "").toLowerCase().includes(term) ||
      (u.workplace || "").toLowerCase().includes(term) ||
      (u.email || "").toLowerCase().includes(term)
    ));

    const recentUids = new Set(activeChats.map(chat => chat.participants.find(p => p !== user.uid)).filter(Boolean) as string[]);
    const recent = allUsers.filter(u => recentUids.has(u.uid));

    return { recent, directory };
  }, [allUsers, activeChats, searchText, user, isVerified, userProfile]);

  const isUserOnline = (u: UserProfile) => u.lastSeenAt ? (Date.now() - u.lastSeenAt) < PRESENCE_THRESHOLD_MS : false;

  const MessageBubble = memo(({ msg }: { msg: PeerMessage }) => {
    const isMe = msg.senderId === user?.uid;
    const [timeLeft, setTimeLeft] = useState<string>('');

    useEffect(() => {
      if (!msg.expiresAt) return;
      const update = () => {
        const diff = msg.expiresAt! - Date.now();
        if (diff <= 0) return setTimeLeft('00:00');
        const h = Math.floor(diff / (1000 * 60 * 60));
        const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeLeft(`${h}h ${m}m`);
      };
      update();
      const interval = setInterval(update, 60000);
      return () => clearInterval(interval);
    }, [msg.expiresAt]);

    return (
      <motion.div 
        initial={{ opacity: 0, y: 10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className={`flex ${isMe ? 'justify-end' : 'justify-start'} mb-4`}
      >
        <div className={`max-w-[85%] rounded-2xl overflow-hidden shadow-sm border ${isMe ? 'bg-indigo-600 text-white border-indigo-500 rounded-tr-none' : (isDarkMode ? 'bg-[#2a2d34] border-gray-700 text-gray-100' : 'bg-white border-gray-100 text-gray-800') + ' rounded-tl-none'}`}>
          
          {msg.sharedContent?.type === 'IMAGE' && (
            <div className="relative group">
              <img src={msg.sharedContent.data} alt="Adjunto" className="w-full h-auto max-h-[300px] object-contain bg-black/10" />
              {msg.expiresAt && (
                <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-md px-2 py-1 rounded-full flex items-center gap-1.5 border border-white/20">
                  <Clock size={10} className="text-amber-400" />
                  <span className="text-[9px] font-black text-white uppercase tracking-tighter">{timeLeft}</span>
                </div>
              )}
            </div>
          )}

          {msg.sharedContent?.type === 'REPORT' && (
            <div className={`p-3 border-b ${isMe ? 'bg-indigo-700 border-indigo-800' : (isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-blue-50 border-blue-100')}`}>
               <div className="flex items-center gap-2 mb-2">
                  <FileText size={14} className="text-blue-500"/>
                  <p className="text-[10px] font-black truncate uppercase">{msg.sharedContent.title}</p>
               </div>
               {!isMe && (
                 <button onClick={() => { saveReportToCloud(user!.uid, {...msg.sharedContent!.data, content: sanitizeHtml(msg.sharedContent!.data?.content || ''), id: Date.now().toString(), createdAt: Date.now(), updatedAt: Date.now()}); alert("Plantilla importada a su biblioteca."); }} className="w-full py-1.5 bg-blue-600 text-white rounded-lg text-[8px] font-black uppercase flex items-center justify-center gap-2 transition-all active:scale-95">Importar</button>
               )}
            </div>
          )}

          {msg.text && <div className="px-3 py-2 whitespace-pre-wrap leading-relaxed font-medium text-[13px]">{msg.text}</div>}
          <div className={`px-3 pb-1.5 text-[8px] text-right font-black tracking-widest ${isMe ? 'text-indigo-200' : 'text-gray-400'}`}>
            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </motion.div>
    );
  });

  // Memoized Message List to prevent flickering on input/sending state changes
  const MemoizedMessages = useMemo(() => {
    if (selectedUser) {
      return messages.map(m => <MessageBubble key={m.id} msg={m} />);
    }
    return groupMessages.map(m => (
      <motion.div 
        key={m.id} 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex ${m.senderId === user?.uid ? 'justify-end' : 'justify-start'} mb-4`}
      >
        <div className={`max-w-[85%] rounded-2xl overflow-hidden shadow-sm border ${m.senderId === user?.uid ? 'bg-indigo-600 text-white border-indigo-500 rounded-tr-none' : (isDarkMode ? 'bg-[#2a2d34] border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-800') + ' rounded-tl-none'}`}>
          {m.senderId !== user?.uid && (
            <div className="px-3 pt-2 text-[9px] font-black uppercase text-indigo-400 tracking-widest">{m.senderName}</div>
          )}
          {m.sharedContent?.type === 'IMAGE' && <img src={m.sharedContent.data} alt="Adjunto" className="w-full h-auto max-h-[300px] object-contain bg-black/10" />}
          {m.sharedContent?.type === 'REPORT' && (
            <div className={`p-3 border-b ${m.senderId === user?.uid ? 'bg-indigo-700 border-indigo-800' : (isDarkMode ? 'bg-gray-800/40 border-gray-700' : 'bg-blue-50 border-blue-100')}`}>
               <div className="flex items-center gap-2 mb-2">
                  <FileText size={14} className="text-blue-500"/>
                  <p className="text-[10px] font-black truncate uppercase">{m.sharedContent.title}</p>
               </div>
               {m.senderId !== user?.uid && (
                 <button onClick={() => { saveReportToCloud(user!.uid, {...m.sharedContent!.data, content: sanitizeHtml(m.sharedContent!.data?.content || ''), id: Date.now().toString(), createdAt: Date.now(), updatedAt: Date.now()}); alert("Se ha importado la plantilla a su biblioteca personal."); }} className="w-full py-1.5 bg-blue-600 text-white rounded-lg text-[8px] font-black uppercase flex items-center justify-center gap-2 transition-all active:scale-95">Importar</button>
               )}
            </div>
          )}
          {m.text && <div className="px-3 py-2 whitespace-pre-wrap leading-relaxed font-medium text-[13px]">{m.text}</div>}
          <div className={`px-3 pb-1.5 text-[8px] text-right font-black tracking-widest ${m.senderId === user?.uid ? 'text-indigo-200' : 'text-gray-400'}`}>
            {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </motion.div>
    ));
  }, [messages, groupMessages, selectedUser, user?.uid, isDarkMode]);

  const renderDirectory = () => (
    <div className="flex flex-col h-full animate-in fade-in duration-300">
       <div className={`px-6 py-4 border-b flex justify-between items-center sticky top-0 z-40 shadow-sm shrink-0 ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className="flex flex-col">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-xl shadow-sm">
                <Users size={18} className="text-white" />
              </div>
              <h1 className={`font-['Outfit'] font-black text-xl tracking-tight uppercase ${isDarkMode ? 'text-gray-100' : 'text-black'}`}>COMUNIDAD</h1>
            </div>
            {connectionStatus && (
              <div className={`flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-full w-fit text-[8px] font-black uppercase tracking-widest ${connectionStatus.success ? 'text-emerald-500 bg-emerald-500/10' : 'text-amber-500 bg-amber-500/10'}`}>
                {connectionStatus.success ? 'Cloud Online' : 'Local Only'}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button 
               onClick={() => setIsSettingsModalOpen(true)}
               className={`p-2 rounded-xl border transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50'}`}
             >
                <Settings size={18} />
            </button>
            <button 
               onClick={toggleDarkMode}
               className={`p-2 rounded-xl border transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-yellow-400' : 'bg-white border-gray-200 text-gray-400 hover:bg-gray-50'}`}
             >
                {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
       </div>

       <div className="px-4 py-2 flex gap-2 border-b overflow-x-auto shrink-0">
          <button onClick={() => setActiveTab('CHATS')} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'CHATS' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:bg-gray-100'}`}>Mensajes</button>
          <button onClick={() => setActiveTab('GROUPS')} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'GROUPS' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-400 hover:bg-gray-100'}`}>Grupos</button>
          {activeTab === 'GROUPS' && (
            <button onClick={() => setIsCreateGroupModalOpen(true)} className="ml-auto p-2 bg-emerald-500 text-white rounded-xl shadow-sm active:scale-95 transition-all">
              <Plus size={16} />
            </button>
          )}
       </div>

       <div className="p-4 border-b">
          <div className="relative">
             <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
             <input type="text" placeholder={activeTab === 'CHATS' ? "Buscar colega o especialidad..." : "Buscar grupo..."} className={`w-full pl-9 pr-3 py-2.5 text-sm border rounded-xl outline-none transition-all ${isDarkMode ? 'bg-[#2a2d34] border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`} value={searchText} onChange={e => setSearchText(e.target.value)} />
          </div>
       </div>

       <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-6">
          {activeTab === 'CHATS' ? (
            <>
              {!isVerified && (
                <div className="p-6 bg-amber-50 border border-amber-100 rounded-3xl mb-4 animate-in zoom-in duration-500 shadow-sm">
                   <div className="flex items-center gap-3 text-amber-600 mb-2">
                      <ShieldCheck size={20} />
                      <p className="text-xs font-black uppercase tracking-widest">Validación en Curso</p>
                   </div>
                   <p className="text-[11px] text-amber-800 leading-relaxed font-medium">
                      Tu perfil está siendo revisado por el sistema de seguridad de MODULAR. Una vez validado por el Dr. Francisco Reyes o el sistema automático, podrás ver al resto de la comunidad y enviar mensajes clínicos.
                   </p>
                </div>
              )}

              {directoryError && (
                <div className="p-6 bg-red-50 border border-red-100 rounded-3xl mb-4 animate-in zoom-in duration-500 shadow-sm">
                   <div className="flex items-center gap-3 text-red-600 mb-2">
                      <ShieldAlert size={20} />
                      <p className="text-xs font-black uppercase tracking-widest">Error de Conexión</p>
                   </div>
                   <p className="text-[11px] text-red-800 leading-relaxed font-medium">
                      {directoryError}
                   </p>
                   <p className="text-[9px] text-red-400 mt-2 italic">
                      Este error suele indicar un problema de permisos en la base de datos. Si eres el administrador, verifica las Security Rules.
                   </p>
                   <button 
                    onClick={() => setRetryCount(prev => prev + 1)}
                    className="mt-4 w-full py-2 bg-red-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-700 transition-all active:scale-95 shadow-lg shadow-red-600/20"
                  >
                    Reintentar Sincronización
                  </button>
                </div>
              )}

              {isDirectoryLoading && (
                <div className="py-10 flex flex-col items-center justify-center opacity-30 animate-pulse">
                    <RefreshCw size={32} className="animate-spin mb-2" />
                    <p className="text-[10px] font-black uppercase tracking-widest">Sincronizando Colegas...</p>
                </div>
              )}

              {contactSections.recent.length > 0 && !searchText && (
                <div>
                   <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-2 mb-3">Recientes</p>
                   {contactSections.recent.map(u => {
                     const online = isUserOnline(u);
                     const rank = getUserRank(u);
                     const highRank = isHighRank(u);
                     return (
                       <button key={u.uid} onClick={() => setSelectedUser(u)} className={`w-full text-left p-3 mb-1 rounded-2xl flex items-center gap-3 transition-all ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
                          <div className="relative shrink-0">
                             <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-xs ${isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'}`}>{u.photoURL ? <img src={u.photoURL} className="w-full h-full rounded-full object-cover" alt="" /> : (u.displayName || u.email || 'U').substring(0,2).toUpperCase()}</div>
                             <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-[3px] ${isDarkMode ? 'border-[#2a2d34]' : 'border-white'} ${online ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`}></div>
                          </div>
                          <div className="min-w-0 flex-1">
                             <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5 min-w-0">
                                   <p className={`text-sm font-black truncate uppercase ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{u.displayName || u.email || u.uid.substring(0,8)}</p>
                                   {highRank && <Crown size={10} className="text-amber-500 shrink-0" />}
                                </div>
                                <div className="flex items-center gap-1 shrink-0 ml-2">
                                   <div className={`w-1.5 h-1.5 rounded-full ${u.status === 'AVAILABLE' ? 'bg-emerald-500' : u.status === 'READING' ? 'bg-blue-500' : u.status === 'ON_CALL' ? 'bg-amber-500' : 'bg-slate-400'}`} />
                                   <span className={`text-[8px] font-black uppercase tracking-tighter ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      {u.status === 'AVAILABLE' ? 'Disponible' : u.status === 'READING' ? 'Informando' : u.status === 'ON_CALL' ? 'De Guardia' : 'No Disponible'}
                                   </span>
                                </div>
                             </div>
                             <p className={`text-[9px] font-black uppercase tracking-tighter ${highRank ? 'text-amber-500' : 'text-blue-500'}`}>{rank}</p>
                             <p className={`text-[8px] font-bold truncate uppercase text-gray-400`}>{u.mainSpecialty || 'Médico Radiólogo'}</p>
                          </div>
                       </button>
                     );
                   })}
                </div>
              )}

              <div>
                 <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest px-2 mb-3">{searchText ? 'Resultados' : 'Directorio MODULAR'}</p>
                 {contactSections.directory.length === 0 && !isDirectoryLoading && (
                    <div className="py-20 text-center opacity-20">
                        <Users size={48} className="mx-auto mb-2" />
                        <p className="text-[10px] font-black uppercase">Sin resultados</p>
                    </div>
                 )}
                 {contactSections.directory.map(u => {
                    const online = isUserOnline(u);
                    const rank = getUserRank(u);
                    const highRank = isHighRank(u);
                    return (
                      <button key={u.uid} onClick={() => setSelectedUser(u)} className={`w-full text-left p-3 mb-1 rounded-2xl flex items-center gap-3 transition-all ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}>
                         <div className="relative shrink-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-xs shrink-0 ${isDarkMode ? 'bg-gray-700 text-gray-500' : 'bg-gray-200 text-gray-400'}`}>{u.photoURL ? <img src={u.photoURL} className="w-full h-full rounded-full object-cover" alt="" /> : (u.displayName || u.email || 'U').substring(0,2).toUpperCase()}</div>
                            <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-[3px] ${isDarkMode ? 'border-[#2a2d34]' : 'border-white'} ${online ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`}></div>
                         </div>
                         <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between">
                               <div className="flex items-center gap-1.5 min-w-0">
                                  <p className={`text-sm font-black truncate uppercase ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{u.displayName || u.email || u.uid.substring(0,8)}</p>
                                  {u.isVerified && <ShieldCheck size={12} className="text-emerald-500 shrink-0" />}
                                  {highRank && <Crown size={10} className="text-amber-500 shrink-0" />}
                               </div>
                               <div className="flex items-center gap-1 shrink-0 ml-2">
                                  <div className={`w-1.5 h-1.5 rounded-full ${u.status === 'AVAILABLE' ? 'bg-emerald-500' : u.status === 'READING' ? 'bg-blue-500' : u.status === 'ON_CALL' ? 'bg-amber-500' : 'bg-slate-400'}`} />
                                  <span className={`text-[8px] font-black uppercase tracking-tighter ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                     {u.status === 'AVAILABLE' ? 'Disponible' : u.status === 'READING' ? 'Informando' : u.status === 'ON_CALL' ? 'De Guardia' : 'No Disponible'}
                                  </span>
                               </div>
                            </div>
                            <p className={`text-[9px] font-black uppercase tracking-tighter ${highRank ? 'text-amber-500' : 'text-blue-500'}`}>{rank}</p>
                            <p className={`text-[8px] font-bold truncate uppercase text-gray-400`}>{u.mainSpecialty || 'Especialista'}</p>
                         </div>
                      </button>
                    );
                 })}
              </div>
            </>
           ) : (
            <div className="space-y-8">
              <div>
                <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest px-2 mb-3">Tus Grupos Médicos</p>
                {workGroups.length === 0 && (
                  <div className="py-10 text-center opacity-30 border-2 border-dashed rounded-3xl mx-2">
                      <Users size={32} className="mx-auto mb-2" />
                      <p className="text-[9px] font-black uppercase">Aún no eres miembro de ningún grupo</p>
                  </div>
                )}
                {workGroups.filter(g => g.name.toLowerCase().includes(searchText.toLowerCase())).map(g => {
                  const isAdminOfGroup = g.admins.includes(user?.uid || '');
                  const requests = pendingRequests[g.id] || [];
                  return (
                    <div key={g.id} className="relative group">
                      <button 
                        onClick={() => setSelectedGroup(g)} 
                        className={`w-full text-left p-4 mb-2 rounded-2xl flex items-center gap-4 transition-all border ${isDarkMode ? 'bg-gray-800/40 border-gray-700 hover:bg-gray-800' : 'bg-white border-gray-100 hover:bg-gray-50'}`}
                      >
                         <div className="w-12 h-12 rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 font-black text-lg shrink-0">
                            {g.name.substring(0,1).toUpperCase()}
                         </div>
                         <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-black truncate uppercase ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{g.name}</p>
                              {isAdminOfGroup && <Crown size={12} className="text-amber-500" />}
                            </div>
                            <p className="text-[11px] text-indigo-500 font-medium truncate mt-1">
                               {g.lastMessage ? (
                                 <><span className="font-black">{g.lastMessage.senderName}:</span> {g.lastMessage.text}</>
                               ) : "Inicia la conversación clínica..."}
                            </p>
                         </div>
                         {isAdminOfGroup && requests.length > 0 && (
                            <div className="bg-red-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full animate-pulse">
                              {requests.length} PENDIENTES
                            </div>
                         )}
                      </button>
                      {isAdminOfGroup && (
                        <button 
                          onClick={(e) => { e.stopPropagation(); setSelectedGroupToManage(g); setIsGroupManagementOpen(true); }}
                          className="absolute -top-1 -right-1 p-1.5 bg-slate-800 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 shadow-lg"
                        >
                          <Settings size={12} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div>
                <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest px-2 mb-3">Directorio de Grupos Públicos</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pb-10">
                  {publicGroups.filter(g => !workGroups.find(myG => myG.id === g.id)).map(g => (
                    <div key={g.id} className={`p-4 rounded-2xl border flex flex-col gap-3 ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-100'}`}>
                      <div className="flex items-center gap-3">
                         <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 font-black">
                            {g.name.substring(0,1).toUpperCase()}
                         </div>
                         <div className="min-w-0 flex-1">
                            <p className={`text-xs font-black truncate uppercase ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{g.name}</p>
                            <p className="text-[9px] font-bold text-gray-400 uppercase">Admin: {allUsers.find(u => g.admins.includes(u.uid))?.displayName || 'Médico'}</p>
                         </div>
                      </div>
                      <p className="text-[10px] text-gray-500 line-clamp-2 italic tracking-tight leading-relaxed">{g.description || "Sin descripción disponible."}</p>
                      <button 
                        onClick={() => handleJoinPublicGroup(g)}
                        disabled={!isVerified}
                        className={`w-full py-2 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${!isVerified ? 'bg-gray-200 dark:bg-gray-800 text-gray-400 cursor-not-allowed' : 'bg-indigo-600/10 text-indigo-600 hover:bg-indigo-600 hover:text-white'}`}
                      >
                        {!isVerified ? "Requiere validación" : "Solicitar Unión"}
                      </button>
                    </div>
                  ))}
                  {publicGroups.length === 0 && (
                    <div className="col-span-full py-10 text-center opacity-20 italic text-[10px] uppercase font-black">No hay grupos públicos disponibles</div>
                  )}
                </div>
              </div>
            </div>
          )}
       </div>

       {/* Modal de Creación de Grupo */}
       <Modal isOpen={isCreateGroupModalOpen} onClose={() => setIsCreateGroupModalOpen(false)} title="NUEVO GRUPO DE TRABAJO">
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="text-[10px] font-black text-gray-400 uppercase">Nombre del Grupo</label>
                <button 
                 onClick={() => setNewGroupIsPublic(!newGroupIsPublic)}
                 className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border transition-all ${newGroupIsPublic ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}
                >
                  {newGroupIsPublic ? 'PÚBLICO' : 'PRIVADO'}
                </button>
              </div>
              <input type="text" value={newGroupName} onChange={e => setNewGroupName(e.target.value)} className={`w-full p-3 rounded-xl border outline-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`} placeholder="Ej: Comité de Neuro" />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase mb-1 block">Descripción</label>
              <textarea value={newGroupDesc} onChange={e => setNewGroupDesc(e.target.value)} className={`w-full p-3 rounded-xl border outline-none resize-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`} rows={2} placeholder="Propósito del grupo..." />
            </div>
            
            <p className="text-[9px] text-gray-400 font-medium italic opacity-70">Nota: Al crear un grupo, serás el administrador inicial. Puedes transferir este mando más tarde.</p>

            <button onClick={handleCreateGroup} disabled={!newGroupName.trim()} className={`w-full py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${newGroupName.trim() ? 'bg-indigo-600 text-white shadow-lg active:scale-95' : 'bg-gray-200 text-gray-400'}`}>Crear Grupo Clínico</button>
          </div>
       </Modal>

       {/* Modal de Gestión de Grupo (Admin Only) */}
       <Modal isOpen={isGroupManagementOpen} onClose={() => setIsGroupManagementOpen(false)} title={`GESTIÓN: ${selectedGroupToManage?.name}`}>
          <div className="space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar pr-2">
             <div>
               <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-4">Solicitudes Pendientes</p>
               <div className="space-y-3">
                 {selectedGroupToManage && (pendingRequests[selectedGroupToManage.id] || []).length === 0 && (
                   <p className="text-[10px] text-gray-400 font-bold uppercase italic p-4 text-center border-2 border-dashed rounded-2xl">No hay solicitudes pendientes</p>
                 )}
                 {selectedGroupToManage && (pendingRequests[selectedGroupToManage.id] || []).map(req => (
                   <div key={req.id} className="flex items-center gap-3 p-3 rounded-2xl border bg-gray-50 dark:bg-gray-800/40">
                      <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 font-black text-[10px] shrink-0 uppercase">
                         {req.userName.substring(0,2)}
                      </div>
                      <div className="min-w-0 flex-1">
                         <p className="text-[11px] font-black uppercase truncate">{req.userName}</p>
                         <p className="text-[9px] text-gray-400 font-bold uppercase truncate">{req.userEmail}</p>
                      </div>
                      <div className="flex items-center gap-1">
                         <button 
                           onClick={() => handleGroupJoinRequest(req.id, 'ACCEPTED', selectedGroupToManage.id, req.userId)}
                           className="p-1.5 bg-emerald-500 text-white rounded-lg shadow-sm"
                         >
                           <Check size={14} />
                         </button>
                         <button 
                           onClick={() => handleGroupJoinRequest(req.id, 'REJECTED')}
                           className="p-1.5 bg-red-500 text-white rounded-lg shadow-sm"
                         >
                           <X size={14} />
                         </button>
                      </div>
                   </div>
                 ))}
               </div>
             </div>

             <div>
               <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-4">Traspaso de Mando (Administrador)</p>
               <p className="text-[9px] text-gray-400 mb-3 italic">Elige un sucesor. Él deberá aceptar el mando antes de que puedas dejar de ser administrador.</p>
               <div className="flex items-center gap-2 mb-4">
                 <select 
                   className={`flex-1 p-2.5 rounded-xl border text-[11px] font-bold outline-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-200'}`}
                   value={selectedGroupToManage?.pendingAdminId || ''}
                   onChange={(e) => selectedGroupToManage && proposeAdminTransfer(selectedGroupToManage.id, e.target.value)}
                 >
                   <option value="">Seleccionar sucesor...</option>
                   {allUsers.filter(u => selectedGroupToManage?.members.includes(u.uid) && !selectedGroupToManage?.admins.includes(u.uid)).map(u => (
                     <option key={u.uid} value={u.uid}>{u.displayName || u.email}</option>
                   ))}
                 </select>
               </div>
               {selectedGroupToManage?.pendingAdminId && (
                 <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-700 rounded-xl">
                   <p className="text-[9px] text-amber-700 dark:text-amber-400 font-black uppercase">Traspaso Pendiente</p>
                   <p className="text-[8px] text-amber-600 dark:text-amber-500 font-bold uppercase">Esperando que {allUsers.find(u => u.uid === selectedGroupToManage.pendingAdminId)?.displayName} acepte el cargo.</p>
                 </div>
               )}
             </div>

             <div className="pt-4 border-t">
               <button 
                 onClick={() => {
                   if (selectedGroupToManage && window.confirm("¿Seguro que deseas salir del grupo?")) {
                     leaveWorkGroup(selectedGroupToManage.id, user!.uid);
                     setIsGroupManagementOpen(false);
                     setSelectedGroup(null);
                   }
                 }}
                 className="w-full py-3 bg-red-500/10 text-red-500 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all"
               >
                 Abandonar Grupo
               </button>
             </div>
          </div>
       </Modal>

       {/* Modal de Configuración de Notificaciones */}
       <Modal isOpen={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} title="NOTIFICACIONES COMUNIDAD">
          <div className="space-y-6">
            <div className="flex items-center justify-between p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg text-blue-600">
                  <MessageSquare size={18} />
                </div>
                <div>
                  <p className="text-xs font-black uppercase">Mensajes Directos</p>
                  <p className="text-[9px] text-gray-400 font-bold uppercase">Notificaciones flotantes P2P</p>
                </div>
              </div>
              <button onClick={() => updateNotificationSettings({ communityToasts: !(userProfile?.notificationSettings?.communityToasts !== false) })} className={`p-1.5 rounded-lg transition-all ${userProfile?.notificationSettings?.communityToasts !== false ? 'text-emerald-500' : 'text-gray-400'}`}>
                {userProfile?.notificationSettings?.communityToasts !== false ? <Bell size={20} /> : <BellOff size={20} />}
              </button>
            </div>

            <div className="flex items-center justify-between p-4 rounded-2xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg text-indigo-600">
                  <Users size={18} />
                </div>
                <div>
                  <p className="text-xs font-black uppercase">Mensajes de Grupo</p>
                  <p className="text-[9px] text-gray-400 font-bold uppercase">Notificaciones flotantes de grupos</p>
                </div>
              </div>
              <button onClick={() => updateNotificationSettings({ groupToasts: !(userProfile?.notificationSettings?.groupToasts !== false) })} className={`p-1.5 rounded-lg transition-all ${userProfile?.notificationSettings?.groupToasts !== false ? 'text-emerald-500' : 'text-gray-400'}`}>
                {userProfile?.notificationSettings?.groupToasts !== false ? <Bell size={20} /> : <BellOff size={20} />}
              </button>
            </div>

            {workGroups.length > 0 && (
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-2">Silenciar Grupos Específicos</p>
                <div className="space-y-2">
                  {workGroups.map(g => {
                    const isMuted = userProfile?.notificationSettings?.mutedGroups?.includes(g.id);
                    return (
                      <div key={g.id} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 dark:border-gray-700">
                        <span className="text-[11px] font-black uppercase">{g.name}</span>
                        <button onClick={() => toggleGroupMute(g.id)} className={`p-1.5 rounded-lg transition-all ${isMuted ? 'text-red-500' : 'text-emerald-500'}`}>
                          {isMuted ? <BellOff size={16} /> : <Bell size={16} />}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
       </Modal>
    </div>
  );

  const RenderChat = () => {
    const targetName = selectedUser?.displayName || selectedGroup?.name || 'Chat';
    const targetRank = selectedUser ? getUserRank(selectedUser) : 'Grupo de Trabajo';
    const isTargetHighRank = selectedUser ? isHighRank(selectedUser) : false;
    const isTargetOnline = selectedUser ? isUserOnline(selectedUser) : true;
    const targetPhoto = selectedUser?.photoURL;
    const currentMessages = selectedUser ? messages : groupMessages;

    return (
      <div className="flex flex-col h-full animate-in slide-in-from-right-10 duration-300">
         <div className={`px-4 py-4 border-b flex items-center gap-3 sticky top-0 z-40 shadow-sm shrink-0 ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-gray-200'}`}>
            <button onClick={() => { setSelectedUser(null); setSelectedGroup(null); }} className="p-2 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50/10 rounded-lg transition-all"><ArrowLeft size={20}/></button>
            <div className="relative shrink-0">
               <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-xs ${isDarkMode ? 'bg-gray-800 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>{targetPhoto ? <img src={targetPhoto} className="w-full h-full rounded-full object-cover" alt="" /> : targetName.substring(0,2).toUpperCase()}</div>
               {selectedUser && <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-[3px] ${isDarkMode ? 'border-[#2a2d34]' : 'border-white'} ${isTargetOnline ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`}></div>}
            </div>
            <div className="flex-1 min-w-0">
               <div className="flex items-center gap-1.5">
                  <h3 className={`text-[14px] font-black truncate uppercase tracking-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{targetName}</h3>
                  {isTargetHighRank && <Crown size={12} className="text-amber-500 shrink-0" />}
               </div>
               <p className={`text-[9px] font-black uppercase tracking-widest leading-none ${isTargetHighRank ? 'text-amber-500' : (isTargetOnline ? 'text-emerald-500' : 'text-gray-400')}`}>
                  {targetRank}
               </p>
            </div>
            {selectedGroup?.pendingAdminId === user?.uid && (
              <div className="flex items-center gap-2 animate-pulse pr-4">
                 <p className="text-[10px] font-black uppercase text-amber-500">¿Aceptar Mando?</p>
                 <button 
                  onClick={() => acceptAdminRole(selectedGroup.id, user!.uid)}
                  className="bg-emerald-500 text-white p-1.5 rounded-lg shadow-md"
                 >
                   <Check size={14} />
                 </button>
              </div>
            )}
            <div className="flex items-center gap-1">
              {selectedGroup && (
                <button onClick={() => toggleGroupMute(selectedGroup.id)} className={`p-2 rounded-xl transition-all ${userProfile?.notificationSettings?.mutedGroups?.includes(selectedGroup.id) ? 'text-red-500' : 'text-emerald-500'}`}>
                  {userProfile?.notificationSettings?.mutedGroups?.includes(selectedGroup.id) ? <BellOff size={18} /> : <Bell size={18} />}
                </button>
              )}
              <button 
                 onClick={toggleDarkMode}
                 className={`p-2 rounded-xl border transition-all ${isDarkMode ? 'bg-gray-700 border-gray-600 text-yellow-400' : 'bg-white border-gray-200 text-gray-400'}`}
               >
                  {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            </div>
         </div>

         <div className="flex-1 overflow-y-auto custom-scrollbar p-4 bg-transparent">
            {currentMessages.length === 0 && (
               <div className="h-full flex flex-col items-center justify-center opacity-10">
                  <MessageSquare size={64} />
                  <p className="text-[10px] font-black uppercase mt-2">Inicio de hilo clínico</p>
               </div>
            )}
            <AnimatePresence initial={false}>
              {MemoizedMessages}
            </AnimatePresence>
            <div ref={messagesEndRef} />
         </div>

         <div className={`p-3 border-t pb-20 md:pb-6 ${isDarkMode ? 'bg-[#1e2024] border-gray-700' : 'bg-white border-gray-200'}`}>
             <ChatInput 
               onSendMessage={(text) => handleSendMessage(text)} 
               isDarkMode={isDarkMode} 
               isSending={isSending} 
               onOpenAttachment={() => setIsAttachmentModalOpen(true)} 
               disabled={!isVerified}
             />
         </div>

         <Modal isOpen={isAttachmentModalOpen} onClose={() => setIsAttachmentModalOpen(false)} title="ADJUNTOS CLÍNICOS">
            <div className="flex flex-col max-h-[60vh]">
               <div className="flex border-b mb-4">
                  <button onClick={() => setAttachmentTab('REPORTS')} className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all ${attachmentTab === 'REPORTS' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400'}`}>Biblioteca</button>
                  <button onClick={() => setAttachmentTab('IMAGES')} className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all ${attachmentTab === 'IMAGES' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-400'}`}>Cámara</button>
               </div>
               
               {attachmentTab === 'REPORTS' ? (
                  <div className="overflow-y-auto p-1 space-y-2 custom-scrollbar">
                     {reports.length === 0 && <p className="text-center py-10 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Sin plantillas</p>}
                     {reports.filter(r => !r.deletedAt).map(r => (
                       <button key={r.id} onClick={() => handleSendMessage('', { type: 'REPORT', data: r, title: r.title })} className={`w-full text-left p-3 rounded-xl border flex items-center gap-3 transition-all ${isDarkMode ? 'bg-gray-800 border-gray-700 hover:bg-gray-700' : 'bg-white hover:bg-indigo-50 border-gray-100 shadow-sm'}`}>
                          <FileText size={14} className="text-blue-500 shrink-0"/>
                          <span className="text-[11px] font-black uppercase truncate">{r.title}</span>
                       </button>
                     ))}
                  </div>
               ) : (
                  <div className="flex flex-col items-center py-6 space-y-6">
                     <div className="p-6 bg-indigo-50 dark:bg-indigo-900/20 rounded-[32px] border-2 border-dashed border-indigo-200 flex flex-col items-center">
                        <ImageIcon size={40} className="text-indigo-500 mb-3" />
                        <p className="text-[10px] font-black text-indigo-600 text-center uppercase tracking-widest mb-1">Captura Efímera (24h)</p>
                        <p className="text-[8px] font-bold text-gray-500 text-center uppercase tracking-widest max-w-[150px]">Se comprimen al 60% automáticamente.</p>
                     </div>
                     <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
                     <button onClick={() => fileInputRef.current?.click()} className="px-8 py-3 bg-indigo-600 text-white rounded-xl font-black text-[10px] uppercase shadow-lg tracking-[0.2em] active:scale-95 transition-all">Seleccionar Imagen</button>
                  </div>
               )}
            </div>
         </Modal>
      </div>
    );
  };

  return (
    <div className={`flex flex-col h-full transition-colors relative overflow-hidden font-['Outfit'] ${isDarkMode ? 'bg-[#1e2024]' : 'bg-gray-50'}`}>
       {(selectedUser || selectedGroup) ? <RenderChat /> : renderDirectory()}
    </div>
  );
};

export default MessagingScreen;
